import { AxisKind, MappingSystem, MatchMode, ValueType } from '@atlas/types'
import { z } from 'zod'

// A `code` anywhere in the classification core is a stable slug — the identity
// used for matching, mappings, filters, and URLs, NOT a display string. Symbols
// (®/™/©), spaces, slashes, and uppercase are rejected; the `label`/`name`
// carries the human-readable form. Mirrors the seeded codes (e.g. label
// 'ACTIVYL®' → code 'activyl', 'Geography' → 'geography').
const slugCode = z
	.string()
	.min(1)
	.regex(/^[a-z0-9_]+$/, 'code must be a lowercase slug: letters, digits, and underscores only')

// ─── Axis ─────────────────────────────────────────────────────────────────────

export const axisCreateSchema = z.object({
	code: slugCode,
	name: z.string().min(1),
	kind: z.enum(AxisKind),
	valueType: z.enum(ValueType).default(ValueType.LIST),
	isHierarchical: z.boolean().default(false),
})

export const axisUpdateSchema = z
	.object({
		// code is immutable (stable); only display + flags are editable
		name: z.string().min(1),
		valueType: z.enum(ValueType),
		isHierarchical: z.boolean(),
		status: z.string().min(1),
	})
	.partial()

// ─── Axis value ────────────────────────────────────────────────────────────────

export const axisValueCreateSchema = z.object({
	code: slugCode,
	label: z.string().min(1),
	parentId: z.string().optional(),
	meta: z.record(z.string(), z.unknown()).optional(),
})

export const axisValueUpdateSchema = z
	.object({
		// code is immutable; label is display-only and freely editable
		label: z.string().min(1),
		meta: z.record(z.string(), z.unknown()),
		status: z.string().min(1),
	})
	.partial()

export const axisValueReparentSchema = z.object({
	parentId: z.string().nullable(), // null re-parents to root
})

/**
 * Fold one value into another. `into` is the value that SURVIVES; the one addressed by
 * the route is retired. Both must sit on the same axis — a merge across axes would move
 * classifications onto a value that means something else entirely.
 */
export const axisValueMergeSchema = z.object({
	into: z.string().min(1),
})

// ─── Value mapping (geo-scoped) ──────────────────────────────────────────────

export const valueMappingCreateSchema = z.object({
	system: z.enum(MappingSystem),
	geoValueId: z.string().nullable().optional(), // null = worldwide default
	externalCode: z.string().min(1),
})

/** The most codes one reverse lookup resolves, and the most rows one batch writes. */
export const VALUE_MAPPING_BATCH_MAX = 500

/** `POST /value-mappings/resolve`: external codes of one system, back to one axis's values. */
export const externalCodeResolveSchema = z.object({
	system: z.enum(MappingSystem),
	axisCode: z.string().min(1),
	codes: z.array(z.string().min(1)).min(1).max(VALUE_MAPPING_BATCH_MAX),
	geoValueId: z.string().regex(/^\d+$/, 'must be a value id').optional(),
})

const batchMappingSchema = z.object({
	valueId: z.string().regex(/^\d+$/, 'must be a value id'),
	system: z.enum(MappingSystem),
	geoValueId: z.string().regex(/^\d+$/, 'must be a value id').nullable().optional(),
	externalCode: z.string().trim().min(1),
})

/** `POST /value-mappings/batch`: mappings written and removed in one transaction. */
export const valueMappingBatchSchema = z
	.object({
		upserts: z.array(batchMappingSchema).default([]),
		deletes: z.array(batchMappingSchema).default([]),
	})
	.refine((batch) => batch.upserts.length + batch.deletes.length > 0, {
		message: 'must write or remove at least one mapping',
		path: ['upserts'],
	})
	.refine((batch) => batch.upserts.length + batch.deletes.length <= VALUE_MAPPING_BATCH_MAX, {
		message: `must hold at most ${VALUE_MAPPING_BATCH_MAX} mappings`,
		path: ['upserts'],
	})

// ─── Availability ─────────────────────────────────────────────────────────────

export const availabilitySetSchema = z.object({
	geoValueIds: z.array(z.string()), // empty = available everywhere
})

// ─── Resolution inputs ─────────────────────────────────────────────────────────

const axisSelectionSchema = z.record(z.string(), z.array(z.string()))

// Tuple ids are ordinals within an object (1, 2, …) — a non-positive or
// fractional value is never a tuple, so reject it at the boundary rather than
// letting it reach a PK column.
const tupleId = z.number().int().positive()

/**
 * CLASSIFY — add ONE new assertion to an object.
 *
 * Carries no `tupleId`: the id is allocated server-side inside the write's own
 * locked transaction, precisely so a caller cannot supply a client-computed
 * "next" ordinal and race a concurrent add into the same tuple. To change an
 * assertion that already exists, use replace; to delete one, use remove.
 */
export const classifyInputSchema = z.object({
	objectType: z.string().min(1),
	objectId: z.string().min(1),
	tuple: axisSelectionSchema,
})

/** One already-flattened tag row — the shape the bulk variant transports. */
export const classifyRowSchema = z.object({
	objectType: z.string().min(1),
	objectId: z.string().min(1),
	axisCode: z.string().min(1),
	valueCode: z.string().min(1),
	tupleId: tupleId.optional(),
})

/**
 * CLASSIFY BATCH — bulk-classify MANY objects in one transaction, for pipelines
 * (upload, work-order seeding) that would otherwise issue one call per object.
 * Additive and idempotent, so a retried batch converges instead of duplicating.
 *
 * A row may name its own `tupleId`, which is how a caller reproduces an exact
 * prior state (re-seeding, migration). Interactive callers should not: they want
 * `classify`, which allocates safely.
 */
export const classifyBatchInputSchema = z.object({
	tuples: z.array(classifyRowSchema).min(1),
})

/** REMOVE TUPLE — delete ONE assertion, leaving the object's siblings intact. */
export const removeTupleInputSchema = z.object({
	objectType: z.string().min(1),
	objectId: z.string().min(1),
	tupleId,
})

export const matchingInputSchema = z.object({
	objectType: z.string().min(1),
	context: axisSelectionSchema,
	mode: z.enum(MatchMode).default(MatchMode.LENIENT),
})

// Explain one object's coverage decision: the matching question, asked for a
// single object and answered with reasons. Same shape as `matchingInputSchema`
// plus the object being explained.
export const explainMatchInputSchema = matchingInputSchema.extend({
	objectId: z.string().min(1),
})

// `filters` selects on DIMENSION axes (matched against classification tags, subtree-aware).
export const searchInputSchema = z.object({
	objectType: z.string().min(1),
	filters: axisSelectionSchema,
	// Optional object_attribute-equality filters ({ attributeAxisCode → values }),
	// AND-combined with `filters` (ADR-0007). Omitted ⇒ no attribute constraint.
	attributeFilters: axisSelectionSchema.optional(),
})

// ─── Facet (active-values drilldown) ──────────────────────────────────────────

export const facetInputSchema = z.object({
	axisCode: z.string().min(1),
	filter: axisSelectionSchema.default({}),
	// OPTIONAL: omitted ⇒ count objects of any type. Nav levels that face across
	// all object types omit it; a type-specific leaf (e.g. product) passes one.
	objectType: z.string().min(1).optional(),
	// OPTIONAL: axis code to count DISTINCT values of instead of distinct objects
	// (e.g. facet geography, countBy 'brand' ⇒ each region's count is distinct
	// brands reachable under it). Omitted ⇒ counts distinct objects.
	countBy: z.string().min(1).optional(),
	// OPTIONAL: an object only qualifies if it has a tag on EVERY listed axis
	// (e.g. requireAxes ['brand'] ⇒ only brand-carrying objects count).
	requireAxes: z.array(z.string().min(1)).optional(),
})

// ─── Batch facet (skip-self, non-admin) ──────────────────────────────────────

export const facetBatchInputSchema = z.object({
	filter: axisSelectionSchema.default({}),
	axes: z.array(z.string().min(1)).min(1),
	objectType: z.string().min(1).optional(),
	requireAxes: z.array(z.string().min(1)).optional(),
	// OPTIONAL: narrows candidates to this id set BEFORE counting — for a caller
	// folding in a constraint the axis `filter` can't express (e.g. a host
	// table's own non-axis-backed field). Empty array means "nothing matches".
	candidateObjectIds: z.array(z.string().min(1)).optional(),
})

// ─── Set attribute (object_attribute write path) ─────────────────────────────

export const setAttributeInputSchema = z.object({
	objectType: z.string().min(1),
	objectId: z.string().min(1),
	axisCode: z.string().min(1),
	// A multi-select attribute stores a set of value-codes (a brand in 2 BUs →
	// ['companion_animals','poultry']); the writer dedupes. A scalar attribute
	// (text/url/date) passes a single-element array.
	valueCodes: z.array(z.string().min(1)).min(1),
})

// ─── Brand (promoted object) ──────────────────────────────────────────────────

export const brandCreateSchema = z.object({
	code: slugCode,
	name: z.string().min(1),
	description: z.string().optional(),
	logoRef: z.string().optional(),
	businessUnit: z.string().optional(),
	meta: z.record(z.string(), z.unknown()).optional(),
})

export const brandUpdateSchema = z
	.object({
		// code is immutable (mirrors the axis_value code)
		name: z.string().min(1),
		description: z.string().nullable(),
		logoRef: z.string().nullable(),
		businessUnit: z.string().nullable(),
		meta: z.record(z.string(), z.unknown()),
		status: z.string().min(1),
	})
	.partial()

// ─── Product (promoted object) ────────────────────────────────────────────────

export const productCreateSchema = z.object({
	code: slugCode,
	name: z.string().min(1),
	brandId: z.string().min(1),
	subtitle: z.string().optional(),
	therapeuticClass: z.string().optional(),
	principalClaim: z.string().optional(),
	durationSeal: z.string().optional(),
	imageRef: z.string().optional(),
	meta: z.record(z.string(), z.unknown()).optional(),
})

export const productUpdateSchema = z
	.object({
		name: z.string().min(1),
		subtitle: z.string().nullable(),
		therapeuticClass: z.string().nullable(),
		principalClaim: z.string().nullable(),
		durationSeal: z.string().nullable(),
		imageRef: z.string().nullable(),
		meta: z.record(z.string(), z.unknown()),
		status: z.string().min(1),
	})
	.partial()

// Batch object-tags read: resolve the classification tags of many objects at once.
export const tagsBatchInputSchema = z.object({
	objectType: z.string().min(1),
	objectIds: z.array(z.string().min(1)).min(1),
})

// Batch object-attributes read: resolve the object_attribute values (e.g. order_code)
// of many objects on the requested attribute axes at once. Separate from the tags
// read because attributes live in `object_attribute`, not the dimension `classification`.
export const objectAttributesBatchInputSchema = z.object({
	objectType: z.string().min(1),
	objectIds: z.array(z.string().min(1)).min(1),
	axisCodes: z.array(z.string().min(1)).min(1),
})

// ─── Remove object (anchor soft-delete) ──────────────────────────────────────
// Soft-delete a classified object's anchor. Both fields are required so a missing
// or malformed body is a 400, not a silent no-op.
export const removeObjectInputSchema = z.object({
	objectType: z.string().min(1),
	objectId: z.string().min(1),
})

// ─── Inferred types ─────────────────────────────────────────────────────────────

export type AxisCreateInput = z.infer<typeof axisCreateSchema>
export type AxisUpdateInput = z.infer<typeof axisUpdateSchema>
export type AxisValueCreateInput = z.infer<typeof axisValueCreateSchema>
export type AxisValueUpdateInput = z.infer<typeof axisValueUpdateSchema>
export type AxisValueReparentInput = z.infer<typeof axisValueReparentSchema>
export type AxisValueMergeInput = z.infer<typeof axisValueMergeSchema>
export type ValueMappingCreateInput = z.infer<typeof valueMappingCreateSchema>
export type AvailabilitySetInput = z.infer<typeof availabilitySetSchema>
export type ExternalCodeResolveInput = z.infer<typeof externalCodeResolveSchema>
export type ValueMappingBatchInput = z.infer<typeof valueMappingBatchSchema>
export type ValueMappingBatchRow = z.infer<typeof batchMappingSchema>
export type ClassifyInputDTO = z.infer<typeof classifyInputSchema>
export type ClassifyRowDTO = z.infer<typeof classifyRowSchema>
export type ClassifyBatchInputDTO = z.infer<typeof classifyBatchInputSchema>
export type RemoveTupleInputDTO = z.infer<typeof removeTupleInputSchema>
export type MatchingInputDTO = z.infer<typeof matchingInputSchema>
export type ExplainMatchInputDTO = z.infer<typeof explainMatchInputSchema>
export type SearchInputDTO = z.infer<typeof searchInputSchema>
export type FacetInputDTO = z.infer<typeof facetInputSchema>
export type FacetBatchInputDTO = z.infer<typeof facetBatchInputSchema>
export type SetAttributeInputDTO = z.infer<typeof setAttributeInputSchema>
export type BrandCreateInput = z.infer<typeof brandCreateSchema>
export type BrandUpdateInput = z.infer<typeof brandUpdateSchema>
export type ProductCreateInput = z.infer<typeof productCreateSchema>
export type ProductUpdateInput = z.infer<typeof productUpdateSchema>
export type TagsBatchInputDTO = z.infer<typeof tagsBatchInputSchema>
export type ObjectAttributesBatchInputDTO = z.infer<typeof objectAttributesBatchInputSchema>
export type RemoveObjectInputDTO = z.infer<typeof removeObjectInputSchema>
